// SPDX-License-Identifier: AGPL-3.0-or-later

import {Logger} from '@app/features/platform/utils/AppLogger';
import {
	createInitialVoiceStats,
	createVoiceStatsSnapshot,
	selectVoiceStatsCollectionDecision,
	transitionVoiceStatsSnapshot,
	type VoiceStatsEvent,
	type VoiceStatsRtpCounter,
	type VoiceStatsSnapshot,
} from '@app/features/voice/engine/VoiceStatsStateMachine';
import {asVoiceConnectionQuality, VoiceConnectionQuality} from '@app/features/voice/engine/VoiceTrackSource';
import {
	classifyVideoDecoderAcceleration,
	classifyVideoEncoderAcceleration,
	type VideoAccelerationStatus,
} from '@app/features/voice/utils/VideoAccelerationClassification';
import type {Room} from 'livekit-client';
import {makeAutoObservable, runInAction} from 'mobx';

const logger = new Logger('VoiceStatsManager');

export interface VoiceStats {
	audioSendBitrate: number;
	audioRecvBitrate: number;
	videoSendBitrate: number;
	videoRecvBitrate: number;
	audioPacketLoss: number;
	videoPacketLoss: number;
	rtt: number;
	jitter: number;
	participantCount: number;
	duration: number;
}

export interface LatencyDataPoint {
	timestamp: number;
	latency: number;
}

export interface PerTrackStats {
	direction: 'send' | 'recv';
	kind: 'audio' | 'video' | 'unknown';
	ssrc?: number;
	rid?: string;
	mid?: string;
	trackIdentifier?: string;
	mediaSourceId?: string;
	codec?: string;
	payloadType?: number;
	bitrateKbps: number;
	packetsLost?: number;
	packetsLossPercent?: number;
	jitterMs?: number;
	framesPerSecond?: number;
	sourceFramesPerSecond?: number;
	frameWidth?: number;
	frameHeight?: number;
	sourceFrameWidth?: number;
	sourceFrameHeight?: number;
	sourceFrames?: number;
	framesEncoded?: number;
	framesDecoded?: number;
	framesDropped?: number;
	framesSent?: number;
	freezeCount?: number;
	totalFreezesDurationMs?: number;
	totalEncodeTimeMs?: number;
	encoderImplementation?: string;
	powerEfficientEncoder?: boolean;
	encoderAcceleration?: VideoAccelerationStatus;
	scalabilityMode?: string;
	qualityLimitationReason?: string;
	qualityLimitationResolutionChanges?: number;
	targetBitrateKbps?: number;
	totalPacketSendDelayMs?: number;
	nackCount?: number;
	pliCount?: number;
	firCount?: number;
	retransmittedPacketsSent?: number;
	retransmittedBytesSent?: number;
	keyFramesEncoded?: number;
	keyFramesDecoded?: number;
	decoderImplementation?: string;
	powerEfficientDecoder?: boolean;
	decoderAcceleration?: VideoAccelerationStatus;
	totalDecodeTimeMs?: number;
	jitterBufferDelayMs?: number;
	jitterBufferEmittedCount?: number;
	concealedSamples?: number;
	silentConcealedSamples?: number;
	totalSamplesReceived?: number;
}

export interface TransportInfo {
	candidatePairState?: string;
	localCandidateType?: string;
	localProtocol?: string;
	localNetworkType?: string;
	remoteCandidateType?: string;
	remoteProtocol?: string;
	currentRoundTripTimeMs?: number;
	availableOutgoingBitrate?: number;
	availableIncomingBitrate?: number;
	dtlsState?: string;
	iceState?: string;
	selectedCandidatePairChanges?: number;
	dtlsCipher?: string;
	srtpCipher?: string;
	tlsVersion?: string;
	networkType?: string;
}

export interface VoiceStatsSample {
	timestamp: number;
	rtt: number;
	jitter: number;
	audioPacketLoss: number;
	videoPacketLoss: number;
	audioSendBitrate: number;
	audioRecvBitrate: number;
	videoSendBitrate: number;
	videoRecvBitrate: number;
}

type RoomWithEngine = Room & {
	engine?: {
		client?: {
			rtt?: number;
		};
		pcManager?: {
			publisher?: StatsSource;
			subscriber?: StatsSource;
		};
	};
};

interface StatsSource {
	getStats(): Promise<StatsReportMap>;
}

interface StatsReportMap {
	values(): IterableIterator<unknown>;
}

interface RTCStatsEntry {
	type: string;
	id: string;
	kind?: string;
	mediaType?: string;
	bytesSent?: number;
	bytesReceived?: number;
	packetsSent?: number;
	packetsLost?: number;
	packetsReceived?: number;
	jitter?: number;
	state?: string;
	currentRoundTripTime?: number;
	availableOutgoingBitrate?: number;
	availableIncomingBitrate?: number;
	codecId?: string;
	mimeType?: string;
	payloadType?: number;
	ssrc?: number;
	rid?: string;
	mid?: string;
	trackId?: string;
	trackIdentifier?: string;
	mediaSourceId?: string;
	framesPerSecond?: number;
	frameWidth?: number;
	frameHeight?: number;
	width?: number;
	height?: number;
	frames?: number;
	framesEncoded?: number;
	framesDecoded?: number;
	framesDropped?: number;
	framesSent?: number;
	freezeCount?: number;
	totalFreezesDuration?: number;
	totalEncodeTime?: number;
	encoderImplementation?: string;
	powerEfficientEncoder?: boolean;
	scalabilityMode?: string;
	qualityLimitationReason?: string;
	qualityLimitationResolutionChanges?: number;
	targetBitrate?: number;
	totalPacketSendDelay?: number;
	localCandidateId?: string;
	remoteCandidateId?: string;
	candidateType?: string;
	protocol?: string;
	networkType?: string;
	nominated?: boolean;
	selected?: boolean;
	dtlsState?: string;
	iceState?: string;
	selectedCandidatePairChanges?: number;
	selectedCandidatePairId?: string;
	nackCount?: number;
	pliCount?: number;
	firCount?: number;
	retransmittedPacketsSent?: number;
	retransmittedBytesSent?: number;
	keyFramesEncoded?: number;
	keyFramesDecoded?: number;
	decoderImplementation?: string;
	powerEfficientDecoder?: boolean;
	totalDecodeTime?: number;
	jitterBufferDelay?: number;
	jitterBufferEmittedCount?: number;
	concealedSamples?: number;
	silentConcealedSamples?: number;
	totalSamplesReceived?: number;
	dtlsCipher?: string;
	srtpCipher?: string;
	tlsVersion?: string;
	address?: string;
	ip?: string;
	port?: number;
}

const LATENCY_UPDATE_INTERVAL_MS = 2000;
const STATS_UPDATE_INTERVAL_MS = 2000;

function normalizeTrackKind(kind: string | undefined): PerTrackStats['kind'] {
	if (kind === 'audio' || kind === 'video') return kind;
	return 'unknown';
}

function getReportKind(report: RTCStatsEntry, reportsById: Map<string, RTCStatsEntry>): PerTrackStats['kind'] {
	const directKind = normalizeTrackKind(report.kind ?? report.mediaType);
	if (directKind !== 'unknown') return directKind;
	const codec = report.codecId ? reportsById.get(report.codecId) : undefined;
	if (codec?.mimeType?.startsWith('audio/')) return 'audio';
	if (codec?.mimeType?.startsWith('video/')) return 'video';
	const track = report.trackId ? reportsById.get(report.trackId) : undefined;
	const mediaSource = report.mediaSourceId ? reportsById.get(report.mediaSourceId) : undefined;
	return normalizeTrackKind(track?.kind ?? track?.mediaType ?? mediaSource?.kind ?? mediaSource?.mediaType);
}

function getBitrateKbps(
	reportId: string,
	currentBytes: number | undefined,
	now: number,
	rtpCounters: Map<string, VoiceStatsRtpCounter>,
): number {
	if (typeof currentBytes !== 'number' || !Number.isFinite(currentBytes)) {
		return 0;
	}
	const previous = rtpCounters.get(reportId);
	let bitrateKbps = 0;
	if (previous?.bytes !== undefined) {
		const dt = (now - previous.timestamp) / 1000;
		const db = currentBytes - previous.bytes;
		if (dt > 0 && db >= 0) bitrateKbps = (db * 8) / 1000 / dt;
	}
	rtpCounters.set(reportId, {
		...previous,
		bytes: currentBytes,
		timestamp: now,
	});
	return bitrateKbps;
}

function getPacketLossPercent(
	reportId: string,
	report: RTCStatsEntry,
	now: number,
	rtpCounters: Map<string, VoiceStatsRtpCounter>,
): number | undefined {
	if (typeof report.packetsLost !== 'number' || typeof report.packetsReceived !== 'number') {
		return undefined;
	}
	const previous = rtpCounters.get(reportId);
	let lostPackets = report.packetsLost;
	let receivedPackets = report.packetsReceived;
	if (previous?.packetsLost !== undefined && previous.packetsReceived !== undefined) {
		const lostDelta = report.packetsLost - previous.packetsLost;
		const receivedDelta = report.packetsReceived - previous.packetsReceived;
		if (lostDelta >= 0 && receivedDelta >= 0 && lostDelta + receivedDelta > 0) {
			lostPackets = lostDelta;
			receivedPackets = receivedDelta;
		}
	}
	rtpCounters.set(reportId, {
		...previous,
		packetsLost: report.packetsLost,
		packetsReceived: report.packetsReceived,
		timestamp: now,
	});
	const totalPackets = receivedPackets + lostPackets;
	return totalPackets > 0 ? (lostPackets / totalPackets) * 100 : undefined;
}

async function collectFromStatsSource(
	source: StatsSource,
	sourceId: string,
	now: number,
	rtpCounters: Map<string, VoiceStatsRtpCounter>,
	activeCounterIds: Set<string>,
): Promise<{
	tracks: Array<PerTrackStats>;
	rtt: number;
	transport: TransportInfo | null;
}> {
	const reports = await source.getStats();
	const reportsById = new Map<string, RTCStatsEntry>();
	for (const report of reports.values()) {
		const statsEntry = report as RTCStatsEntry;
		reportsById.set(statsEntry.id, statsEntry);
	}
	const tracks: Array<PerTrackStats> = [];
	let rtt = 0;
	let activePair: RTCStatsEntry | null = null;
	let transportReport: RTCStatsEntry | null = null;
	for (const raw of reports.values()) {
		const report = raw as RTCStatsEntry;
		if (
			report.type === 'candidate-pair' &&
			report.state === 'succeeded' &&
			(report.selected || report.nominated !== false)
		) {
			const crt = report.currentRoundTripTime;
			if (crt) rtt = Math.max(rtt, crt * 1000);
			if (
				!activePair ||
				(typeof report.availableOutgoingBitrate === 'number' && !activePair.availableOutgoingBitrate)
			) {
				activePair = report;
			}
			continue;
		}
		if (report.type === 'transport') {
			transportReport = report;
			continue;
		}
		const isOutbound = report.type === 'outbound-rtp';
		const isInbound = report.type === 'inbound-rtp';
		if (!isOutbound && !isInbound) continue;
		const direction = isOutbound ? 'send' : 'recv';
		const id = `${sourceId}:${report.type}:${report.id}`;
		activeCounterIds.add(id);
		const currentBytes = isOutbound ? report.bytesSent : report.bytesReceived;
		const bitrateKbps = getBitrateKbps(id, currentBytes, now, rtpCounters);
		const codec = report.codecId ? reportsById.get(report.codecId) : undefined;
		const mediaSource = report.mediaSourceId ? reportsById.get(report.mediaSourceId) : undefined;
		const packetsLossPercent = isInbound ? getPacketLossPercent(id, report, now, rtpCounters) : undefined;
		const kind = getReportKind(report, reportsById);
		const encoderAcceleration =
			isOutbound && kind === 'video'
				? classifyVideoEncoderAcceleration(report.encoderImplementation, report.powerEfficientEncoder)
				: undefined;
		const decoderAcceleration =
			isInbound && kind === 'video'
				? classifyVideoDecoderAcceleration(report.decoderImplementation, report.powerEfficientDecoder)
				: undefined;
		tracks.push({
			direction,
			kind,
			ssrc: report.ssrc,
			rid: report.rid,
			mid: report.mid,
			trackIdentifier: report.trackIdentifier ?? mediaSource?.trackIdentifier,
			mediaSourceId: report.mediaSourceId,
			codec: codec?.mimeType,
			payloadType: codec?.payloadType,
			bitrateKbps: Math.round(bitrateKbps),
			packetsLost: report.packetsLost,
			packetsLossPercent: packetsLossPercent !== undefined ? Math.round(packetsLossPercent * 10) / 10 : undefined,
			jitterMs: typeof report.jitter === 'number' ? Math.round(report.jitter * 1000 * 10) / 10 : undefined,
			framesPerSecond: report.framesPerSecond,
			sourceFramesPerSecond: mediaSource?.framesPerSecond,
			frameWidth: report.frameWidth,
			frameHeight: report.frameHeight,
			sourceFrameWidth: mediaSource?.frameWidth ?? mediaSource?.width,
			sourceFrameHeight: mediaSource?.frameHeight ?? mediaSource?.height,
			sourceFrames: mediaSource?.frames,
			framesEncoded: report.framesEncoded,
			framesDecoded: report.framesDecoded,
			framesDropped: report.framesDropped,
			framesSent: report.framesSent,
			freezeCount: report.freezeCount,
			totalFreezesDurationMs:
				typeof report.totalFreezesDuration === 'number' ? Math.round(report.totalFreezesDuration * 1000) : undefined,
			totalEncodeTimeMs:
				typeof report.totalEncodeTime === 'number' ? Math.round(report.totalEncodeTime * 1000) : undefined,
			encoderImplementation: report.encoderImplementation,
			powerEfficientEncoder: report.powerEfficientEncoder,
			encoderAcceleration,
			scalabilityMode: report.scalabilityMode,
			qualityLimitationReason: report.qualityLimitationReason,
			qualityLimitationResolutionChanges: report.qualityLimitationResolutionChanges,
			targetBitrateKbps: typeof report.targetBitrate === 'number' ? Math.round(report.targetBitrate / 1000) : undefined,
			totalPacketSendDelayMs:
				typeof report.totalPacketSendDelay === 'number' ? Math.round(report.totalPacketSendDelay * 1000) : undefined,
			nackCount: report.nackCount,
			pliCount: report.pliCount,
			firCount: report.firCount,
			...(isOutbound
				? {
						retransmittedPacketsSent: report.retransmittedPacketsSent,
						retransmittedBytesSent: report.retransmittedBytesSent,
						keyFramesEncoded: report.keyFramesEncoded,
					}
				: {
						keyFramesDecoded: report.keyFramesDecoded,
						decoderImplementation: report.decoderImplementation,
						powerEfficientDecoder: report.powerEfficientDecoder,
						decoderAcceleration,
						totalDecodeTimeMs:
							typeof report.totalDecodeTime === 'number' ? Math.round(report.totalDecodeTime * 1000) : undefined,
						jitterBufferDelayMs:
							typeof report.jitterBufferDelay === 'number' &&
							typeof report.jitterBufferEmittedCount === 'number' &&
							report.jitterBufferEmittedCount > 0
								? Math.round((report.jitterBufferDelay / report.jitterBufferEmittedCount) * 1000)
								: undefined,
						jitterBufferEmittedCount: report.jitterBufferEmittedCount,
						concealedSamples: report.concealedSamples,
						silentConcealedSamples: report.silentConcealedSamples,
						totalSamplesReceived: report.totalSamplesReceived,
					}),
		});
	}
	if (!activePair && transportReport?.selectedCandidatePairId) {
		const selectedPair = reportsById.get(transportReport.selectedCandidatePairId);
		if (selectedPair?.type === 'candidate-pair') {
			activePair = selectedPair;
		}
	}
	let transport: TransportInfo | null = null;
	if (activePair || transportReport) {
		const local = activePair?.localCandidateId ? reportsById.get(activePair.localCandidateId) : undefined;
		const remote = activePair?.remoteCandidateId ? reportsById.get(activePair.remoteCandidateId) : undefined;
		transport = {
			candidatePairState: activePair?.state,
			localCandidateType: local?.candidateType,
			localProtocol: local?.protocol,
			localNetworkType: local?.networkType,
			remoteCandidateType: remote?.candidateType,
			remoteProtocol: remote?.protocol,
			currentRoundTripTimeMs:
				typeof activePair?.currentRoundTripTime === 'number'
					? Math.round(activePair.currentRoundTripTime * 1000)
					: undefined,
			availableOutgoingBitrate: activePair?.availableOutgoingBitrate,
			availableIncomingBitrate: activePair?.availableIncomingBitrate,
			dtlsState: transportReport?.dtlsState,
			iceState: transportReport?.iceState,
			selectedCandidatePairChanges: transportReport?.selectedCandidatePairChanges,
			dtlsCipher: transportReport?.dtlsCipher,
			srtpCipher: transportReport?.srtpCipher,
			tlsVersion: transportReport?.tlsVersion,
			networkType: local?.networkType,
		};
	}
	return {tracks, rtt, transport};
}

export class VoiceStatsManager {
	private room: Room | null = null;
	private latencyIntervalId: NodeJS.Timeout | null = null;
	private statsIntervalId: NodeJS.Timeout | null = null;
	private statsSnapshot: VoiceStatsSnapshot = createVoiceStatsSnapshot();
	currentLatency: number | null = null;
	averageLatency: number | null = null;
	latencyHistory: Array<LatencyDataPoint> = [];
	voiceStats: VoiceStats = createInitialVoiceStats();
	perTrackStats: Array<PerTrackStats> = [];
	statsTimeSeries: Array<VoiceStatsSample> = [];
	publisherTransport: TransportInfo | null = null;
	subscriberTransport: TransportInfo | null = null;
	connectionStartTime: number | null = null;
	reconnectionCount: number = 0;

	constructor() {
		makeAutoObservable<this, 'statsSnapshot'>(this, {statsSnapshot: false}, {autoBind: true});
	}

	private transitionStats(event: VoiceStatsEvent): void {
		this.statsSnapshot = transitionVoiceStatsSnapshot(this.statsSnapshot, event);
		this.syncFromStatsSnapshot();
	}

	private syncFromStatsSnapshot(): void {
		const context = this.statsSnapshot.context;
		runInAction(() => {
			this.currentLatency = context.currentLatency;
			this.averageLatency = context.averageLatency;
			this.latencyHistory = context.latencyHistory;
			this.voiceStats = context.voiceStats;
			this.perTrackStats = context.perTrackStats;
			this.statsTimeSeries = context.statsTimeSeries;
			this.publisherTransport = context.publisherTransport;
			this.subscriberTransport = context.subscriberTransport;
			this.connectionStartTime = context.connectionStartTime;
			this.reconnectionCount = context.reconnectionCount;
		});
	}

	get estimatedLatency(): number | null {
		if (!this.room) return null;
		const localParticipant = this.room.localParticipant;
		if (!localParticipant) return null;
		const quality = asVoiceConnectionQuality(localParticipant.connectionQuality);
		switch (quality) {
			case VoiceConnectionQuality.Excellent:
				return 30;
			case VoiceConnectionQuality.Good:
				return 60;
			case VoiceConnectionQuality.Poor:
				return 120;
			default:
				return null;
		}
	}

	get displayLatency(): number | null {
		const measured = this.currentLatency;
		return measured !== null ? measured : this.estimatedLatency;
	}

	get duration(): number {
		return this.connectionStartTime ? Math.floor((Date.now() - this.connectionStartTime) / 1000) : 0;
	}

	setRoom(room: Room | null): void {
		this.room = room;
		this.transitionStats({type: 'room.set', roomIdentity: room, now: Date.now()});
	}

	incrementReconnectionCount(): void {
		this.transitionStats({type: 'reconnection.increment'});
	}

	startLatencyTracking(): void {
		this.transitionStats({type: 'latency.start'});
		if (this.latencyIntervalId !== null) {
			return;
		}
		logger.debug('Starting latency tracking');
		this.latencyIntervalId = setInterval(() => {
			const engineWrap = this.room as RoomWithEngine;
			if (!engineWrap?.engine?.client?.rtt) {
				return;
			}
			const rtt = Math.round(engineWrap.engine.client.rtt);
			const timestamp = Date.now();
			this.transitionStats({type: 'latency.sample', timestamp, latency: rtt});
		}, LATENCY_UPDATE_INTERVAL_MS);
	}

	stopLatencyTracking(): void {
		if (this.latencyIntervalId !== null) {
			clearInterval(this.latencyIntervalId);
			this.latencyIntervalId = null;
		}
		this.transitionStats({type: 'latency.stop'});
	}

	startStatsTracking(): void {
		this.transitionStats({type: 'stats.start'});
		if (this.statsIntervalId !== null) {
			return;
		}
		logger.debug('Starting stats tracking');
		this.statsIntervalId = setInterval(async () => {
			const roomSnapshot = this.room;
			const decision = selectVoiceStatsCollectionDecision(this.statsSnapshot);
			if (decision.type !== 'collect' || decision.roomIdentity !== roomSnapshot) return;
			const statsGeneration = decision.generation;
			this.transitionStats({
				type: 'stats.collectionStarted',
				generation: statsGeneration,
				roomIdentity: decision.roomIdentity,
			});
			if (!this.statsSnapshot.context.statsCollectionInFlight) return;
			try {
				const room = roomSnapshot as RoomWithEngine;
				const engine = room?.engine;
				if (!engine?.pcManager) return;
				const publisher = engine.pcManager.publisher;
				const subscriber = engine.pcManager.subscriber;
				if (!publisher && !subscriber) return;
				const now = Date.now();
				const tracks: Array<PerTrackStats> = [];
				const activeCounterIds = new Set<string>();
				const rtpCounters = new Map<string, VoiceStatsRtpCounter>(decision.rtpCounters);
				let rtt = 0;
				let publisherTransport: TransportInfo | null = null;
				let subscriberTransport: TransportInfo | null = null;
				if (publisher) {
					const result = await collectFromStatsSource(publisher, 'publisher', now, rtpCounters, activeCounterIds);
					tracks.push(...result.tracks);
					if (result.rtt > rtt) rtt = result.rtt;
					publisherTransport = result.transport;
				}
				if (subscriber) {
					const result = await collectFromStatsSource(subscriber, 'subscriber', now, rtpCounters, activeCounterIds);
					tracks.push(...result.tracks);
					if (result.rtt > rtt) rtt = result.rtt;
					subscriberTransport = result.transport;
				}
				if (rtt === 0 && engine.client?.rtt) rtt = engine.client.rtt;
				this.transitionStats({
					type: 'stats.collectionSucceeded',
					generation: statsGeneration,
					roomIdentity: decision.roomIdentity,
					timestamp: now,
					participantCount: roomSnapshot?.numParticipants ?? 0,
					rtt,
					tracks,
					publisherTransport,
					subscriberTransport,
					rtpCounters,
					activeCounterIds,
				});
			} catch (error) {
				logger.debug('Error collecting stats', error);
			} finally {
				this.transitionStats({type: 'stats.collectionFinished', generation: statsGeneration});
			}
		}, STATS_UPDATE_INTERVAL_MS);
	}

	stopStatsTracking(): void {
		if (this.statsIntervalId !== null) {
			clearInterval(this.statsIntervalId);
			this.statsIntervalId = null;
		}
		this.transitionStats({type: 'stats.stop'});
	}

	cleanup(): void {
		logger.debug('Cleaning up');
		if (this.latencyIntervalId !== null) {
			clearInterval(this.latencyIntervalId);
			this.latencyIntervalId = null;
		}
		if (this.statsIntervalId !== null) {
			clearInterval(this.statsIntervalId);
			this.statsIntervalId = null;
		}
		this.room = null;
		this.transitionStats({type: 'stats.cleanup'});
	}

	reset(): void {
		if (this.latencyIntervalId !== null) {
			clearInterval(this.latencyIntervalId);
			this.latencyIntervalId = null;
		}
		if (this.statsIntervalId !== null) {
			clearInterval(this.statsIntervalId);
			this.statsIntervalId = null;
		}
		this.room = null;
		this.transitionStats({type: 'stats.reset'});
	}
}
